import { faPen } from '@fortawesome/pro-regular-svg-icons';
import {
    faArrowCircleRight,
    faPen as faPenSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { TextInlineEditorProps } from 'components/inline_edit/TextInlineEditor';
import { StyledNavLink } from 'components/link/StyledNavLink';
import { NavLinkProps } from 'react-router-dom';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

const ArrowLinkContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-grow: 1;
    gap: 0.8rem;
`;

type ArrowLinkProps = NavLinkProps &
    Readonly<{
        /**
         * Optional color for the Arrow Icon.
         * Unless provided, the color will be the link default (blue)
         */
        iconColor?: string | undefined;

        ariaLabel?: string;
    }>;

const StyledNavLinkCell = styled(StyledNavLink)`
    flex-grow: 1;
    border-radius: 5px;
    &:hover, &:focus-visible {
        background-color: ${tokens.action.hover};
    }
    &:active {
        background-color: ${tokens.action.active};
    }
    padding-block: 0.5rem;
`;

export function ArrowLinkEditorRenderer({
    value,
    sizing = 'expand',
    ...rest
}: ArrowLinkProps &
    Pick<TextInlineEditorProps, 'value'> & {
        sizing?: 'expand' | 'fit-content';
    }): React.ReactNode {
    return (
        <ArrowLinkCell sizing={sizing} {...rest}>
            {value}
        </ArrowLinkCell>
    );
}

/**
 * Renders a NavLink with an arrow on the right.
 */
export function EditableArrowLinkCell({
    children,
    iconColor,
    ariaLabel,
    onEditClick,
    ...rest
}: ArrowLinkProps & { onEditClick: undefined | (() => void) }) {
    return (
        <div className="flex flex-row align-center full-width">
            <StyledNavLinkCell aria-label={ariaLabel} {...rest}>
                <div className="flex align-center flex-grow-1">{children}</div>
                <FontAwesomeIcon
                    aria-label="arrow link icon"
                    icon={faArrowCircleRight}
                    color={tokens.text.link}
                    className="mx-10"
                />
            </StyledNavLinkCell>
            <HoverIconButton
                title="Edit"
                disabled={!onEditClick}
                icon={faPen}
                hoverIcon={faPenSolid}
                color={tokens.icon.actionActive}
                hoverColor={tokens.icon.actionHover}
                onClick={onEditClick}
                aria-label="open editor"
                className="f-15"
            />
        </div>
    );
}

/**
 * Renders a NavLink with an arrow on the right.
 */
export function ArrowLinkCell({
    children,
    iconColor,
    ariaLabel,
    sizing = 'expand',
    ...rest
}: ArrowLinkProps & { sizing?: 'expand' | 'fit-content' }) {
    return (
        <StyledNavLinkCell
            aria-label={ariaLabel}
            {...rest}
            className={`align-center ${sizing === 'expand' ? 'flex-grow-1 full-width' : 'inline-flex'}`}
        >
            <div className="flex align-center flex-grow-1">{children}</div>
            <FontAwesomeIcon
                aria-label="arrow link icon"
                icon={faArrowCircleRight}
                color={tokens.text.link}
                className="mx-10"
            />
        </StyledNavLinkCell>
    );
}

export function ArrowLink({ children, iconColor, ariaLabel, ...rest }: ArrowLinkProps) {
    return (
        <ArrowLinkContainer>
            <StyledNavLink aria-label={ariaLabel} {...rest}>
                <div className="flex">{children}</div>
                <FontAwesomeIcon
                    aria-label="arrow link icon"
                    icon={faArrowCircleRight}
                    color={tokens.text.link}
                    className="mx-10"
                />
            </StyledNavLink>
        </ArrowLinkContainer>
    );
}
